import { PortfolioTypeRule } from 'pco-product-service-internal-consumer';
import * as yup from 'yup';
import {
  EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH,
  PORTFOLIO_BASE_FIELDS,
  PORTFOLIO_TYPE_DEPENDENT_FIELDS,
  PortfolioTypeDependentField,
} from './portfolioTypeDependentFields';
import { resolveFieldRules, resolveServicingRoleRule } from './portfolioTypeFieldRules';

const requiredForPortfolioType = (rules: readonly PortfolioTypeRule[], field: PortfolioTypeDependentField) =>
  yup.string().when('portfolioType', {
    is: (portfolioType: string) =>
      resolveFieldRules({ rules, portfolioType: String(portfolioType), isStandaloneCreate: true, isEdit: false })[field]
        .required === true,
    then: yup.string().required(PORTFOLIO_TYPE_DEPENDENT_FIELDS[field].requiredMessage),
    otherwise: yup.string(),
  });

const portfolioTypeDependentFieldSchemas = (rules: readonly PortfolioTypeRule[]) => ({
  ...Object.fromEntries(
    (Object.keys(PORTFOLIO_TYPE_DEPENDENT_FIELDS) as PortfolioTypeDependentField[]).map((field) => [
      field,
      requiredForPortfolioType(rules, field),
    ]),
  ),
  externalAccountNumber: requiredForPortfolioType(rules, 'externalAccountNumber').max(
    EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH,
    `External account number must be at most ${EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH} characters`,
  ),
});

const requiredByRule = (
  rules: readonly PortfolioTypeRule[],
  field: 'sequenceNumber' | 'chargingCurrency',
  isEdit: boolean,
) =>
  yup.string().when('portfolioType', {
    is: (portfolioType: string) =>
      resolveFieldRules({ rules, portfolioType: String(portfolioType), isStandaloneCreate: true, isEdit })[field]
        .required === true,
    then: yup.string().required(PORTFOLIO_BASE_FIELDS[field].requiredMessage),
    otherwise: yup.string(),
  });

const servicingRoleSchema = (
  rules: readonly PortfolioTypeRule[],
  isStandaloneCreate: boolean,
  rocEnabled: boolean,
  requiredMessage: string,
) =>
  isStandaloneCreate
    ? yup.string().when('portfolioType', {
        is: (portfolioType: string) => resolveServicingRoleRule(rules, String(portfolioType)).required === true,
        then: yup.string().required(requiredMessage),
        otherwise: yup.string().nullable(),
      })
    : rocEnabled
      ? yup.string().required(requiredMessage)
      : yup.string().nullable();

const PortfolioCreationSchema = (
  isEdit: boolean,
  isStandaloneCreate: boolean,
  rocEnabled = false,
  portfolioRules: readonly PortfolioTypeRule[] = [],
) => {
  return yup.object({
    ...(isStandaloneCreate ? portfolioTypeDependentFieldSchemas(portfolioRules) : {}),
    referenceCurrency: isStandaloneCreate
      ? yup.string().required(PORTFOLIO_BASE_FIELDS.referenceCurrency.requiredMessage)
      : yup.string(),
    portfolioType: yup.string().required('Account type is required'),
    sequenceNumber: isStandaloneCreate
      ? requiredByRule(portfolioRules, 'sequenceNumber', isEdit)
      : yup.string().required(PORTFOLIO_BASE_FIELDS.sequenceNumber.requiredMessage),
    chargingCurrency: isStandaloneCreate
      ? requiredByRule(portfolioRules, 'chargingCurrency', isEdit)
      : yup.string().required(PORTFOLIO_BASE_FIELDS.chargingCurrency.requiredMessage),
    allocatedBo: yup.string().when('specialBoEnabled', {
      is: true,
      then: yup.string().required(PORTFOLIO_BASE_FIELDS.allocatedBo.requiredMessage),
      otherwise: yup.string(),
    }),
    servicingRoleType: servicingRoleSchema(
      portfolioRules,
      isStandaloneCreate,
      rocEnabled,
      'Servicing role type is required',
    ),
    servicingUser: servicingRoleSchema(
      portfolioRules,
      isStandaloneCreate,
      rocEnabled,
      'Servicing role user is required',
    ),

    cashAccounts: isEdit
      ? yup.array().of(
          yup.object({
            currency: yup.string().required('Currency is required'),
            accountType: yup.string().required('Cash account type is required'),
          }),
        )
      : yup
          .array()
          .of(
            yup.object({
              currency: yup.string().required('Currency is required'),
              accountType: yup.string().required('Cash account type is required'),
            }),
          )
          .min(1, 'At least one cash account is required')
          .required(),
  });
};

export default PortfolioCreationSchema;
